import classNames from 'classnames';
import React, { useMemo } from 'react';
import { Checkbox } from '../../../formControls/Checkbox/Checkbox';
import { DatePicker } from '../../../formControls/DatePicker/DatePicker';
import { Select } from '../../../formControls/Select/Select';
import { DEFAULT_TIME_FORMAT, TIMEZONES } from '../../../../constants/constants';
import { DateHelper } from '../../../../utils';
import { ThirdStepTooltip } from './ThirdStepTooltip';

import styles from './index.module.css';
import {
  DATE_TIME_FIELD_LABELS,
  DATE_TIME_LABEL_VARIANT,
  getQuickScheduleDatePickerPopupContainer,
  getReturnDateFieldLabel,
  getReturnEarliestLabel,
  isScheduleRideLayout,
  QUICK_SCHEDULE_DATE_PICKER_POPUP_CLASS,
} from './stepThreeDateTimeConstants';

export const ReturnDateTimeSection = ({
  pickUpDate,
  returnTimeDate,
  maxPickUpWindowOfTime,
  handleReturnTime,
  disabledDatesForReturnTime,
  disabledDateTime,
  handleUseWindowOfTime,
  handleChangeTZ,
  values,
  disabledValues,
  returnTimeLowerLimit,
  handleReturnTimeLowerLimit,
  disabledDateForReturnHigherLimit,
  disabledDateTimeForReturnHigherLimit,
  minimumRoundTrip,
  isUberRide = false,
  hideFlexibleCheckbox = false,
  labelVariant = DATE_TIME_LABEL_VARIANT.DEFAULT,
  fieldInputClassName,
  selectFieldClassName,
}) => {
  const datePickerClass = fieldInputClassName || undefined;
  const selectClass = selectFieldClassName || fieldInputClassName || undefined;
  const minAvailableReturnDate = useMemo(
    () => DateHelper.addDates(pickUpDate, minimumRoundTrip),
    [pickUpDate,minimumRoundTrip],
  );

  const diabledDatesForReturnField = useMemo(
    () =>
      values?.isReturnDateFlexible
        ? disabledDateForReturnHigherLimit(returnTimeLowerLimit)
        : disabledDatesForReturnTime(pickUpDate),
    [
      values?.isReturnDateFlexible,
      disabledDateForReturnHigherLimit,
      disabledDatesForReturnTime,
      returnTimeLowerLimit,
      pickUpDate,
    ],
  );

  const disabledDateTImeForReturnField = useMemo(
    () =>
      values?.isReturnDateFlexible
        ? disabledDateTimeForReturnHigherLimit
        : disabledDateTime(minAvailableReturnDate, returnTimeDate),
    [
      values?.isReturnDateFlexible,
      disabledDateTimeForReturnHigherLimit,
      disabledDateTime,
      minAvailableReturnDate,
      returnTimeDate,
    ],
  );

  const returnDateFieldDisabled = useMemo(
    () =>
      !values?.returnTime?.timezone ||
      (values?.isReturnDateFlexible && !returnTimeLowerLimit) ||
      disabledValues?.returnTime,
    [values?.returnTime?.timezone, values?.isReturnDateFlexible, returnTimeLowerLimit, disabledValues?.returnTime],
  );

  const scheduleRideLayout = isScheduleRideLayout(labelVariant);

  const datePickerPopupClassName = scheduleRideLayout
    ? QUICK_SCHEDULE_DATE_PICKER_POPUP_CLASS
    : undefined;

  const datePickerGetPopupContainer = scheduleRideLayout
    ? getQuickScheduleDatePickerPopupContainer
    : (trigger) => trigger;

  const returnDateFieldLabel = useMemo(
    () =>
      getReturnDateFieldLabel({
        labelVariant,
        isReturnDateFlexible: values?.isReturnDateFlexible,
      }),
    [values?.isReturnDateFlexible, labelVariant],
  );

  const returnEarliestLabel = useMemo(
    () => getReturnEarliestLabel(labelVariant),
    [labelVariant],
  );

  const returnTimeDefaultDateTime = useMemo(() => new Date().setHours(0, 0, 0, 0), []);

  return values?.isRoundtrip && !disabledValues?.returnTime ? (
    <>
      {!hideFlexibleCheckbox && (
      <div className={styles.windowOfTime}>
        <label className={styles.useWindowOfTime}>
          <Checkbox
            name='isReturnDateFlexible'
            checked={values?.isReturnDateFlexible}
            onChange={handleUseWindowOfTime}
            disabled={disabledValues?.isReturnDateFlexible}
            whenFieldDisabledShowTooltip={Boolean(disabledValues?.isReturnDateFlexible)}
          />
          <span className={styles.checkboxLabelText}>
            {DATE_TIME_FIELD_LABELS.FLEXIBLE_PICKUP_CHECKBOX}
          </span>
        </label>
        <ThirdStepTooltip range={maxPickUpWindowOfTime} useFlexibleHoursMessage />
      </div>
      )}
      <div
        className={classNames(styles.dateAndTime, {
          qsScheduleDateAndTime: scheduleRideLayout,
        })}
      >
        <label
          className={classNames(styles.timezone, {
            qsScheduleTimezone: scheduleRideLayout,
          })}
        >
          <span className={styles.required}>{DATE_TIME_FIELD_LABELS.TIMEZONE}</span>
          <Select
            name='returnTime.timezone'
            options={TIMEZONES}
            onChange={handleChangeTZ}
            placeholder={'Pick one'}
            disabled={disabledValues?.pickUpDate}
            myClassName={selectClass}
            whenFieldDisabledShowTooltip={Boolean(disabledValues?.pickUpDate)}
          />
        </label>

        <div
          className={classNames(styles.range, {
            qsScheduleRange: scheduleRideLayout,
          })}
        >
          {values?.isReturnDateFlexible && (
            <label
              className={classNames(styles.startDate, {
                qsScheduleStartDate: scheduleRideLayout,
              })}
            >
              <span className={styles.required}>{returnEarliestLabel}</span>
              <DatePicker
                name='returnTimeLowerLimit.datetime'
                myClassName={datePickerClass || styles.datepicker}
                onChange={handleReturnTimeLowerLimit}
                format={DEFAULT_TIME_FORMAT}
                value={returnTimeLowerLimit}
                showTime={{ defaultValue: returnTimeDefaultDateTime }}
                showNow={false}
                use12Hours
                disabledDate={disabledDatesForReturnTime(pickUpDate) }
                disabledTime={disabledDateTime(minAvailableReturnDate, returnTimeLowerLimit)}
                disabled={!values?.returnTime?.timezone || !pickUpDate || disabledValues?.returnTime}
                whenFieldDisabledShowTooltip={Boolean(disabledValues?.returnTime)}
                inputReadOnly
                allowClear
                popupClassName={datePickerPopupClassName}
                getPopupContainer={datePickerGetPopupContainer}
              />
            </label>
          )}
          <label
            className={classNames({
              [styles.endDate]: values?.isReturnDateFlexible,
              [styles.datepicker]: !values?.isReturnDateFlexible,
              qsScheduleEndDate:
                scheduleRideLayout && values?.isReturnDateFlexible,
              qsScheduleDatepicker:
                scheduleRideLayout && !values?.isReturnDateFlexible,
            })}
          >
            <span className={styles.required}>{returnDateFieldLabel}</span>
            {/* <span className={styles.midnightNoonMessage}>{Midnight_Noon_Clarification}</span> */}
            <DatePicker
              name='returnTime.datetime'
              myClassName={datePickerClass || styles.datepicker}
              onChange={handleReturnTime}
              format={DEFAULT_TIME_FORMAT}
              value={returnTimeDate}
              showTime={{ defaultValue: returnTimeDefaultDateTime }}
              showNow={false}
              use12Hours
              disabledDate={diabledDatesForReturnField}
              disabledTime={disabledDateTImeForReturnField}
              disabled={returnDateFieldDisabled}
              whenFieldDisabledShowTooltip={Boolean(disabledValues?.returnTime)}
              inputReadOnly
              allowClear
              popupClassName={datePickerPopupClassName}
              getPopupContainer={datePickerGetPopupContainer}
            />
          </label>
        </div>
      </div>
    </>
  ) : null;
};
